import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { Target, Hourglass, Users, Home, Hammer, PenTool, MessageSquare, Award, Percent } from 'lucide-react';
import { FaBullseye, FaCogs, FaCalendarAlt, FaUserCog, FaArrowRight, FaLock, FaCheckCircle } from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import StartSidebar from '../components/StartSidebar';

// Progress bar component
const ProgressBar = ({ value, startPoint, goal, label, color = "bg-blue-500", prefix = "$" }) => {
  const range = goal - startPoint;
  const currentPercentage = Math.min(100, Math.max(0, ((value - startPoint) / range) * 100));
  
  return (
    <div className="mb-6">
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{Math.round(currentPercentage)}% Complete</span>
      </div>
      <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} rounded-full`} 
          style={{ width: `${currentPercentage}%` }}
        ></div>
      </div>
      <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
        <span>{prefix}{startPoint.toLocaleString()}</span>
        <span className="font-semibold">{prefix}{value.toLocaleString()}</span>
        <span>{prefix}{goal.toLocaleString()}</span>
      </div>
    </div>
  );
};

// Metric Box component (non-clickable)
const MetricBox = ({ icon, title, value, subtitle, color = "text-blue-600", compact = false, description = "" }) => {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-md dark:shadow-gray-700 ${compact ? 'p-3' : 'p-6'} h-full relative`}>
      {/* Lock icon in top-right corner */}
      <div className="absolute top-3 right-3">
        <FaLock className="text-gray-400 dark:text-gray-500 text-sm" />
      </div>
      
      <div className={`flex flex-col items-center text-center justify-center h-full ${compact ? 'mb-0' : 'mb-4'}`}>
        <div className={`relative ${compact ? 'mb-2' : 'mb-4'}`}>
          {/* Accent background circle */}
          <div className={`absolute inset-0 ${color.replace('text-', 'bg-').replace('600', '100').replace('400', '100')} dark:${color.replace('text-', 'bg-').replace('600', '900/20').replace('400', '900/20')} rounded-full blur-xl opacity-30`}></div>
          {/* Icon with increased size */}
          <div className={`relative ${color} ${compact ? 'text-2xl' : 'text-3xl'}`}>
            {icon}
          </div>
        </div>
        <h3 className={`font-semibold text-gray-800 dark:text-white ${compact ? 'text-lg' : 'text-xl'} mb-1`}>{title}</h3>
        {description && (
          <p className={`text-sm text-gray-500 dark:text-gray-400 text-center ${compact ? 'mb-1' : 'mb-2'}`}>{description}</p>
        )}
      </div>
      {value && <div className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-1">{value}</div>}
      {subtitle && <div className="text-sm text-gray-500 dark:text-gray-400 text-center">{subtitle}</div>}
    </div>
  );
};

const SalesRevenue = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [revenueData, setRevenueData] = useState(null);
  const [timePeriod, setTimePeriod] = useState('currentYear');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesMetrics, setSalesMetrics] = useState({
    closeRatio: null,
    partnerPrograms: 0,
    salesScripts: 0,
    awards: 0,
    currentProject: null
  });
  
  // Revenue form state
  const [revenueForm, setRevenueForm] = useState({
    twoYearsAgo: '',
    oneYearAgo: '',
    currentYearProjection: ''
  });
  const [showRevenueForm, setShowRevenueForm] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // Load user data from Firestore
  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          
          // Extract sales metrics from user data
          const salesData = data.salesData || {};
          const metrics = {
            closeRatio: null,
            partnerPrograms: 0,
            salesScripts: salesData.salesScripts || 0,
            awards: 0,
            currentProject: salesData.currentProject || {
              title: 'Sales Excellence',
              progress: 0,
              description: 'Optimizing sales processes and expanding referral network'
            }
          };
          setSalesMetrics(metrics);
          
          // Load existing revenue data from businessAssessment structure
          const businessAssessment = data.businessAssessment || {};
          console.log('Loading revenue data from businessAssessment:', businessAssessment);
          
          if (businessAssessment.revenueData) {
            console.log('Loading from businessAssessment.revenueData:', businessAssessment.revenueData);
            setRevenueData(businessAssessment.revenueData);
            setShowRevenueForm(false);
          } else if (data.salesData && (data.salesData.twoYearsAgo || data.salesData.oneYearAgo || data.salesData.currentYearProjection)) {
            // Also check salesData structure for revenue data
            console.log('Loading from salesData structure:', data.salesData);
            const revenueDataFromSales = {
              twoYearsAgo: data.salesData.twoYearsAgo,
              oneYearAgo: data.salesData.oneYearAgo,
              currentYearProjection: data.salesData.currentYearProjection,
              chartData: data.salesData.chartData
            };
            setRevenueData(revenueDataFromSales);
            setShowRevenueForm(false);
          } else {
            console.log('No revenue data found, showing form');
            setShowRevenueForm(true);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [currentUser]);

  // Handle revenue form input changes
  const handleRevenueFormChange = (e) => {
    const { name, value } = e.target;
    // Remove any non-numeric characters and convert to number
    const numericValue = value.replace(/[^0-9]/g, '');
    setRevenueForm(prev => ({
      ...prev,
      [name]: numericValue
    }));
  };

  // Handle revenue form submission
  const handleRevenueSubmit = async () => {
    if (!revenueForm.twoYearsAgo || !revenueForm.oneYearAgo || !revenueForm.currentYearProjection) {
      alert('Please fill in all revenue fields');
      return;
    }

    const currentYear = new Date().getFullYear();
    const chartData = [
      { year: currentYear - 2, value: parseInt(revenueForm.twoYearsAgo) },
      { year: currentYear - 1, value: parseInt(revenueForm.oneYearAgo) },
      { year: currentYear, value: parseInt(revenueForm.currentYearProjection) }
    ];

    const revenueDataToSave = {
      twoYearsAgo: parseInt(revenueForm.twoYearsAgo),
      oneYearAgo: parseInt(revenueForm.oneYearAgo),
      currentYearProjection: parseInt(revenueForm.currentYearProjection),
      chartData
    };

    setRevenueData(revenueDataToSave);

    // Save to both salesData and businessAssessment collections
    if (currentUser) {
      try {
        console.log('Saving revenue data:', revenueDataToSave);
        const updateData = {
          // Save to businessAssessment structure like other sections
          'businessAssessment.revenueData': revenueDataToSave,
          'businessAssessment.revenueCompleted': true,
          'businessAssessmentProgress.revenueDataCompleted': true,
          'businessAssessmentProgress.currentSection': 3, // Set to Sales section (index 3)
          // Also save to salesData structure for lead copying
          'salesData.twoYearsAgo': revenueDataToSave.twoYearsAgo,
          'salesData.oneYearAgo': revenueDataToSave.oneYearAgo,
          'salesData.currentYearProjection': revenueDataToSave.currentYearProjection,
          'salesData.chartData': revenueDataToSave.chartData,
          lastUpdated: new Date().toISOString()
        };
        await updateDoc(doc(db, 'users', currentUser.uid), updateData);
        console.log('Revenue data saved successfully');
        
        // Refresh the data to ensure it's loaded
        const updatedUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (updatedUserDoc.exists()) {
          const updatedData = updatedUserDoc.data();
          console.log('Refreshed user data after save:', updatedData.businessAssessment);
          
          // Update the local state with the saved data
          if (updatedData.businessAssessment && updatedData.businessAssessment.revenueData) {
            setRevenueData(updatedData.businessAssessment.revenueData);
            setShowRevenueForm(false);
            console.log('Updated local state with saved revenue data');
          }
        }
      } catch (error) {
        console.error('Error saving revenue data:', error);
        alert('Error saving revenue data. Please try again.');
        return;
      }
    }

    setShowRevenueForm(false);
  };

  // Handle edit button click
  const handleEditRevenue = () => {
    setIsEditing(true);
    setShowRevenueForm(true);
    
    // Pre-populate the form with existing data
    if (revenueData) {
      setRevenueForm({
        twoYearsAgo: revenueData.twoYearsAgo?.toString() || '',
        oneYearAgo: revenueData.oneYearAgo?.toString() || '',
        currentYearProjection: revenueData.currentYearProjection?.toString() || ''
      });
    }
  };

  // Handle continue button click
  const handleContinue = async () => {
    if (!currentUser) return;
    
    try {
      console.log('Saving revenue completion with data:', revenueData);
      // Save revenue completion status
      const continueUpdateData = {
        'businessAssessment.revenueData': revenueData,
        'businessAssessment.revenueCompleted': true,
        'businessAssessmentProgress.revenueDataCompleted': true,
        'businessAssessmentProgress.currentSection': 3, // Set to Sales section (index 3)
        // Also save to salesData structure for lead copying
        'salesData.twoYearsAgo': revenueData.twoYearsAgo,
        'salesData.oneYearAgo': revenueData.oneYearAgo,
        'salesData.currentYearProjection': revenueData.currentYearProjection,
        'salesData.chartData': revenueData.chartData,
        lastUpdated: new Date().toISOString()
      };
      await updateDoc(doc(db, 'users', currentUser.uid), continueUpdateData);
      console.log('Revenue completion saved successfully');
      
      // Refresh the data to ensure it's loaded
      const updatedUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (updatedUserDoc.exists()) {
        const updatedData = updatedUserDoc.data();
        console.log('Refreshed user data after continue:', updatedData.businessAssessment);
        
        // Update the local state with the saved data
        if (updatedData.businessAssessment && updatedData.businessAssessment.revenueData) {
          setRevenueData(updatedData.businessAssessment.revenueData);
          setShowRevenueForm(false);
          console.log('Updated local state with saved revenue data from continue');
        }
      }
      
      // Navigate back to existing conditions page
      navigate('/start/existing-conditions', { state: { fromSalesRevenue: true } });
    } catch (error) {
      console.error('Error saving revenue completion:', error);
      alert('Error saving data. Please try again.');
    }
  };

  // Helper function to generate chart data
  const generateRevenueChartData = () => {
    if (!revenueData || !revenueData.chartData) {
      return [
        { year: new Date().getFullYear() - 2, value: 0 },
        { year: new Date().getFullYear() - 1, value: 0 },
        { year: new Date().getFullYear(), value: 0 }
      ];
    }

    return revenueData.chartData;
  };

  // Show loading state while data is being fetched
  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <BBALoader size={128} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <StartSidebar />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-8 text-center">Sales</h1>
      
      {/* Row 1: Revenue Data and Goals */}
      <div className="grid grid-cols-4 gap-6 mb-6">
        {/* Revenue Card - 3/4 width */}
        <div className="col-span-3">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-gray-700 h-full">
            <div className="flex items-center justify-center mb-4">
              {/* Centered Title */}
              <div className="flex items-center">
                <Target className="text-red-600 dark:text-red-400 mr-2" size={24} />
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Revenue</h2>
              </div>
            </div>
            
            {showRevenueForm ? (
              /* Revenue Input Form */
              <div className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Revenue {new Date().getFullYear() - 2}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">$</span>
                      <input
                        type="text"
                        name="twoYearsAgo"
                        value={revenueForm.twoYearsAgo}
                        onChange={handleRevenueFormChange}
                        className="pl-7 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date().getFullYear() - 2} revenue`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Revenue {new Date().getFullYear() - 1}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">$</span>
                      <input
                        type="text"
                        name="oneYearAgo"
                        value={revenueForm.oneYearAgo}
                        onChange={handleRevenueFormChange}
                        className="pl-7 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date().getFullYear() - 1} revenue`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {new Date().getFullYear()} Projection
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-gray-500 dark:text-gray-400">$</span>
                      <input
                        type="text"
                        name="currentYearProjection"
                        value={revenueForm.currentYearProjection}
                        onChange={handleRevenueFormChange}
                        className="pl-7 py-3 block w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-800 dark:bg-gray-700 dark:text-white sm:text-sm transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder={`Enter ${new Date().getFullYear()} projection`}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={handleRevenueSubmit}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              /* Revenue Chart Display */
              <div className="mt-6">
                <div className="h-[400px] mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={generateRevenueChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value) => ['$' + value.toLocaleString(), 'Revenue']}
                        labelFormatter={(label) => `Year: ${label}`}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        name="Revenue" 
                        stroke="#DC2626" 
                        strokeWidth={2}
                        dot={{ r: 6 }}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={handleEditRevenue}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleContinue}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Goals Card - 1/4 width */}
        <div className="col-span-1">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-gray-700 hover:shadow-lg dark:hover:shadow-gray-600 transition-shadow duration-200">
            <div className="flex items-center justify-center mb-4">
              <FaBullseye className="text-2xl text-red-600 dark:text-red-400 mr-2" />
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Goals</h2>
            </div>
            <div className="space-y-4">
              <div className="flex flex-col items-center justify-center min-h-[100px]">
                <FaLock className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-2" />
              </div>
            </div>
            
            <div className="w-full mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="w-full px-4 py-2 text-sm text-gray-400 dark:text-gray-500 font-medium rounded-lg transition-colors block text-center">
                View All Goals
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-6">
        <div className="col-span-1">
          <MetricBox
            icon={<Percent className="w-10 h-10" />}
            title="Close Ratio"
            description="Track and analyze your sales conversion rates"
            color="text-red-600 dark:text-red-400"
          />
        </div>
        <div className="col-span-1">
          <MetricBox
            icon={<Users className="w-10 h-10" />}
            title="Partner Programs"
            description="Build and manage your referral network of partners"
            color="text-red-600 dark:text-red-400"
          />
        </div>
        <div className="col-span-1">
          <MetricBox
            icon={<Hammer className="w-10 h-10" />}
            title="Pardon Our Dust"
            description="Create flyers to inform neighbors about your jobs"
            color="text-red-600 dark:text-red-400"
          />
        </div>
        <div className="col-span-1">
          <MetricBox
            icon={<PenTool className="w-10 h-10" />}
            title="Proposals"
            description="Access our template to close more deals"
            color="text-red-600 dark:text-red-400"
          />
        </div>
        <div className="col-span-1">
          <MetricBox
            icon={<MessageSquare className="w-10 h-10" />}
            title="Sales Scripts"
            description="Master proven scripts to improve your sales"
            color="text-red-600 dark:text-red-400"
          />
        </div>
        <div className="col-span-1">
          <MetricBox
            icon={<Award className="w-10 h-10" />}
            title="Awards"
            description="Build credibility through industry recognition"
            color="text-red-600 dark:text-red-400"
          />
        </div>
      </div>

      {/* Schedule Call and Ask a Question Row */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-gray-700 transition-shadow duration-200 relative">
          {/* Lock icon in top-right corner */}
          <div className="absolute top-3 right-3">
            <FaLock className="text-gray-400 dark:text-gray-500 text-sm" />
          </div>
          
          <div className="flex items-center justify-center mb-4">
            <FaCalendarAlt className="text-4xl text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-center mb-4 text-gray-800 dark:text-white">Schedule a Call</h2>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">Book a consultation</p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md dark:shadow-gray-700 transition-shadow duration-200 relative">
          {/* Lock icon in top-right corner */}
          <div className="absolute top-3 right-3">
            <FaLock className="text-gray-400 dark:text-gray-500 text-sm" />
          </div>
          
          <div className="flex items-center justify-center mb-4">
            <FaUserCog className="text-4xl text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-center mb-4 text-gray-800 dark:text-white">Ask a Question</h2>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">Get help from our team</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default SalesRevenue;
